import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import Icon from '../Icon';
import { useAuth } from '../../contexts/AuthContext';
import { getActiveProjectId } from '../../utils/session';

// Project archive/delete UI (shown in the project Settings, Project tab, owner only) — Phase 2 of the multi-tenancy
// initiative. This is real production data for a live project, so it
// follows the project's standing delete-lifecycle rule (archive → trash/
// soft-delete → hard-delete only from trash, see memory
// feedback_delete_lifecycle_archive_trash_hard.md) and defaults to the
// least destructive action.
//
// IMPORTANT: ans-backend has no backend endpoint yet for archiving or deleting
// an entire project/workspace (checked routes/planning/workspaces.ts on
// 2026-09-23 — it only has POST /workspaces, pipeline-stage and
// contract-value routes; nothing that touches a whole project's lifecycle).
// The per-user archive/trash/permanent-delete lifecycle already exists
// (routes/planning/users.ts), so a workspace-level equivalent is a natural
// follow-up, but building it now would mean guessing at an unreviewed
// destructive schema change on live project data — exactly the case the
// project's "prefer to stub over guessing wrong" rule calls out. So this
// panel is intentionally inert: it shows the real, safe UX (type-to-confirm,
// defaults to Archive, hard delete gated far behind it) but the action
// buttons are disabled until a real backend endpoint exists.
//
// TODO(workspace-lifecycle): once ans-backend ships
// PATCH /planning/workspaces/:project_id/archive (+ /trash, /restore) and
// DELETE /planning/workspaces/:project_id/permanent mirroring
// services/planning/users.ts's lifecycle, wire handleArchive/handleDelete
// below to real apiPatch/apiDelete calls and remove the `disabled` props.

const WorkspaceDangerZone: React.FC = () => {
  const { workspaces } = useAuth();
  const [showArchive, setShowArchive] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmArchiveText, setConfirmArchiveText] = useState('');
  const [confirmDeleteText, setConfirmDeleteText] = useState('');

  const activeProjectId = getActiveProjectId();
  const active = workspaces.find(w => w.project_id === activeProjectId) || workspaces[0];
  const projectName = active?.project_name ?? 'this project';

  const archiveMatches = confirmArchiveText.trim() === projectName;
  const deleteMatches = confirmDeleteText.trim() === projectName;

  const closeArchive = () => { setShowArchive(false); setConfirmArchiveText(''); };
  const closeDelete = () => { setShowDelete(false); setConfirmDeleteText(''); };

  return (
    <div className="settings-card settings-card-danger">
      <h4 className="settings-card-title">Archive project</h4>
      <p className="settings-card-hint">
        Archiving hides this project without deleting anything. It can be
        restored later. Deleting is a separate, far more destructive action
        that is not available from this app yet.
      </p>

      <div className="wdz-row">
        <div className="wdz-row-text">
          <strong>Archive project</strong>
          <span>Hide {projectName} from your active projects. Recoverable, nothing is deleted.</span>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setShowArchive(true)}>
          Archive...
        </Button>
      </div>

      <div className="wdz-row">
        <div className="wdz-row-text">
          <strong>Delete project</strong>
          <span>
            Permanently remove {projectName} and all its data. Not available yet.
            This app has no way to reverse it, so it stays disabled until a
            reviewed, staged (archive, trash, permanent) backend flow exists.
          </span>
        </div>
        <Button variant="danger" size="sm" onClick={() => setShowDelete(true)} disabled>
          Delete...
        </Button>
      </div>

      <Modal open={showArchive} onClose={closeArchive} title="Archive project" size="small">
        <p className="cd-description">
          This will archive <strong>{projectName}</strong>. It stops appearing in your
          project switcher but nothing is deleted. An owner can restore it later.
        </p>
        <p className="cd-description">
          Type <strong>{projectName}</strong> to confirm.
        </p>
        <input
          type="text"
          className="wdz-confirm-input"
          value={confirmArchiveText}
          onChange={e => setConfirmArchiveText(e.target.value)}
          placeholder={projectName}
          autoFocus
        />
        <p className="wdz-todo-note">
          <Icon name="helpCircle" size={14} /> The server action for this does not exist
          yet. This shows the intended steps; the button stays disabled until
          PATCH /planning/workspaces/:project_id/archive ships.
        </p>
        <div className="cd-actions">
          <Button variant="ghost" size="sm" onClick={closeArchive}>Cancel</Button>
          <Button variant="danger" size="sm" disabled={!archiveMatches}>
            Archive project
          </Button>
        </div>
      </Modal>

      <Modal open={showDelete} onClose={closeDelete} title="Delete project" size="small">
        <p className="cd-description">
          Deleting <strong>{projectName}</strong> is permanent and cannot be undone from
          this app. This action is intentionally not wired up yet: real project data is
          at stake and there is no reviewed staged (archive, trash, permanent) backend
          flow for whole projects to hang this off of.
        </p>
        <p className="cd-description">
          Type <strong>{projectName}</strong> to confirm you understand. This will not
          submit anything.
        </p>
        <input
          type="text"
          className="wdz-confirm-input"
          value={confirmDeleteText}
          onChange={e => setConfirmDeleteText(e.target.value)}
          placeholder={projectName}
          autoFocus
        />
        <div className="cd-actions">
          <Button variant="ghost" size="sm" onClick={closeDelete}>Cancel</Button>
          <Button variant="danger" size="sm" disabled={!deleteMatches || true}>
            Permanently delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default WorkspaceDangerZone;
